import React, { useEffect, useState } from "react";
import { Image, Spin } from "antd";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaExclamationCircle,
  FaExternalLinkAlt,
  FaFilePdf,
  FaRegClock,
  FaRegFileAlt,
} from "react-icons/fa";
import { cdhaService } from "../../services/cdhaService";
import Loading from "../common/Loading/Loading";
import styles from "./KetQuaCard.module.css";

/**
 * Danh sách kết quả cận lâm sàng dạng thẻ sổ xuống, dựng theo
 * ImagingResultsView của màn CateMedicalExaminationDetail bên mobile.
 *
 * Phần đầu thẻ lấy thẳng từ dữ liệu danh sách (tenDichVu, ngayKq, ketLuan) nên
 * không phải gọi API; chi tiết chỉ tải khi người dùng mở thẻ.
 */

const hasText = (v) => !!(v && String(v).trim());

/** Tách mô tả nhiều dòng thành từng gạch đầu dòng, giống danh sách findings. */
const toFindings = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

const CardBody = ({ externId, fallbackConclusion }) => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  // Ảnh hỏng link thì đổi sang ô "Mở bằng trình duyệt", giống ImageViewerModal
  // bên mobile — ảnh CĐHA lấy từ server HIS nên vẫn có trường hợp không tải được.
  const [failedUrls, setFailedUrls] = useState({});

  useEffect(() => {
    let alive = true;
    if (!externId) {
      setLoading(false);
      return undefined;
    }
    cdhaService
      .getCdhaById(externId)
      .then((res) => alive && setDetail(res))
      .catch(() => alive && setDetail(null))
      .finally(() => alive && setLoading(false));
    // Thẻ có thể bị đóng trước khi API trả về; không setState trên node đã gỡ.
    return () => {
      alive = false;
    };
  }, [externId]);

  if (loading) return <Loading tip="Đang tải dữ liệu..." fullScreen={false} />;

  const conclusion = detail?.ketLuan || fallbackConclusion;
  // Giống mobile: ưu tiên mô tả chi tiết, không có thì lấy kết luận làm nội dung.
  const findings = hasText(detail?.moTa)
    ? toFindings(detail.moTa)
    : toFindings(conclusion);
  // urls: endpoint KetQuaCdha/cdha; imageUrls: mảng chuỗi của WareHouseDetailDto
  // (nguồn ảnh app mobile đang dùng) — nhận cả hai để chỗ nào có ảnh là hiện.
  const gallery = cdhaService.formatGalleryItems(
    detail?.urls ?? detail?.imageUrls,
  );

  return (
    <div className={styles.body}>
      <div className={styles.findingLabel}>MÔ TẢ CHI TIẾT:</div>
      <div className={styles.findingBox}>
        {findings.length > 0 ? (
          findings.map((line, i) => (
            <div key={i} className={styles.findingRow}>
              <span className={styles.bullet}>•</span>
              <span className={styles.findingText}>{line}</span>
            </div>
          ))
        ) : (
          <span className={styles.findingText}>Không có mô tả chi tiết.</span>
        )}
      </div>

      {/* Mô tả và kết luận là hai nội dung khác nhau: khi đã có mô tả thì kết
          luận phải hiện riêng, nếu không nó chỉ còn bản rút gọn 2 dòng ở đầu thẻ.
          Nhãn theo typeCls: xét nghiệm gọi là "Kết quả", CĐHA là "Kết luận". */}
      {hasText(detail?.moTa) && hasText(conclusion) && (
        <>
          <div className={styles.findingLabel}>
            {cdhaService.getPanelTitle(detail?.typeCls).toUpperCase()}:
          </div>
          <div className={styles.conclusionBox}>{conclusion}</div>
        </>
      )}

      {gallery.length > 0 && (
        <div className={styles.imageStrip}>
          {/* Bấm ảnh mở chế độ xem toàn màn hình có đếm "n / tổng", vuốt/mũi tên
              sang ảnh khác và phóng to — đúng bộ thao tác của ImageViewerModal. */}
          <Image.PreviewGroup>
            {gallery.map((img, i) => {
              if (img.isPdf) {
                return (
                  <a
                    key={i}
                    href={img.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.pdfThumb}
                  >
                    <FaFilePdf />
                    <span>Xem PDF</span>
                  </a>
                );
              }

              if (failedUrls[img.original]) {
                return (
                  <a
                    key={i}
                    href={img.original}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.brokenThumb}
                  >
                    <FaRegFileAlt className={styles.brokenIcon} />
                    <span>Mở bằng trình duyệt</span>
                  </a>
                );
              }

              return (
                <Image
                  key={i}
                  src={img.thumbnail}
                  preview={{ src: img.original }}
                  alt={img.description || `Ảnh ${i + 1}`}
                  className={styles.image}
                  rootClassName={styles.imageRoot}
                  placeholder={
                    <div className={styles.imageLoading}>
                      <Spin size="small" />
                    </div>
                  }
                  onError={() =>
                    setFailedUrls((prev) => ({ ...prev, [img.original]: true }))
                  }
                />
              );
            })}
          </Image.PreviewGroup>
        </div>
      )}

      {hasText(detail?.pacsUrl) && (
        <a
          href={detail.pacsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.pacsLink}
        >
          <FaExternalLinkAlt />
          Xem ảnh trên hệ thống PACS
        </a>
      )}
    </div>
  );
};

const KetQuaCard = ({ items = [], Icon, emptyText = "Chưa có dữ liệu." }) => {
  // Mở thẻ mới thì thẻ đang mở tự đóng, giống hành vi bên mobile.
  const [expandedKey, setExpandedKey] = useState(null);

  if (items.length === 0) {
    return <div className={styles.empty}>{emptyText}</div>;
  }

  return (
    <div className={styles.list}>
      {items.map((item, index) => {
        const cardKey = item.externId ?? `item-${index}`;
        const isExpanded = expandedKey === cardKey;
        const conclusion = item.ketLuan;
        // Cùng quy ước với mobile: có kết luận thì gắn nhãn "Ghi chú".
        const isAbnormal = hasText(item.ketLuan);

        return (
          <div
            key={cardKey}
            className={`${styles.card} ${isAbnormal ? styles.cardNote : ""}`}
          >
            <button
              type="button"
              className={`${styles.header} ${
                isExpanded ? styles.headerOpen : ""
              }`}
              onClick={() =>
                setExpandedKey((prev) => (prev === cardKey ? null : cardKey))
              }
            >
              <span className={styles.headerIcon}>
                <Icon />
              </span>

              <span className={styles.headerBody}>
                <span className={styles.titleRow}>
                  <span className={styles.title}>{item.tenDichVu}</span>
                  {isAbnormal != null && (
                    <span
                      className={`${styles.stateTag} ${
                        isAbnormal ? styles.stateNote : styles.stateNormal
                      }`}
                    >
                      {isAbnormal ? <FaExclamationCircle /> : <FaCheckCircle />}
                      {isAbnormal ? "Ghi chú" : "Bình thường"}
                    </span>
                  )}
                </span>

                {isAbnormal && (
                  <span className={styles.impression}>
                    <span className={styles.impressionLabel}>Kết luận: </span>
                    {conclusion}
                  </span>
                )}

                {item.ngayKq && (
                  <span className={styles.dateRow}>
                    <FaRegClock />
                    {item.ngayKq}
                  </span>
                )}
              </span>

              <span className={styles.chevron}>
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </span>
            </button>

            {isExpanded && (
              <CardBody
                externId={item.externId}
                fallbackConclusion={conclusion}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default KetQuaCard;
